import { Controller } from 'stimulus';

let L;

class Map extends Controller {
  connect() {
    Map.loadLeaflet()
      .then((module) => {
        L = module.default;

        this.map = this.createMap();
        this.markers = [];
        this.openMarker = null;

        const items = JSON.parse(this.data.get('items'));

        items.forEach((item) => {
          this.createItem(item);
        });

        if (this.data.has('countries')) {
          const countries = JSON.parse(this.data.get('countries'));

          this.renderGeoJson(countries);
        }

        if (this.data.get('fit-world')) {
          const bounds = L.latLngBounds([[85, 180], [-85, -180]]);
          const wantedZoom = this.map.getBoundsZoom(bounds, true);

          this.map.setZoom(wantedZoom);
        }
      });
  }

  disconnect() {
    if (this.map) {
      this.map.remove();
    }
  }

  createItem(item) {
    const marker = this.createMarker(item.type, item.latitude, item.longitude);

    marker.on('click', () => {
      this.closePopup();

      const popupElement = Map.createPopupElement(item);

      this.element.appendChild(popupElement);

      this.openMarker = marker;

      popupElement.focus();
    });

    this.markers.push(marker);
  }

  createMarker(type, latitude, longitude) {
    return L.marker([parseFloat(latitude), parseFloat(longitude)], {
      icon: Map.createMarkerIcon(type),
    }).addTo(this.map);
  }

  createMap() {
    const worldSouthWest = new L.LatLng(75, -180);
    const worldNorthEast = new L.LatLng(-75, 180);
    const bounds = new L.LatLngBounds(worldSouthWest, worldNorthEast);

    const map = L.map(this.containerTarget, {
      center: this.data.get('center').split(','),
      zoom: this.data.get('zoom'),
      scrollWheelZoom: false,
      maxZoom: this.data.get('max-zoom'),
      minZoom: 3,
      maxBounds: bounds,
      maxBoundsViscosity: 0.75,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      noWrap: true,
      bounds,
    }).addTo(map);

    return map;
  }

  onPopupCloseClick() {
    this.closePopup();
  }

  onWindowKeyup(event) {
    if (event.key === 'Escape' && this.openMarker) {
      this.closePopup();
    }
  }

  closePopup() {
    if (this.hasPopupTarget) {
      if (this.openMarker) {
        const markerElement = this.openMarker.getElement();

        markerElement.focus();

        this.openMarker = null;
      }

      this.popupTarget.parentNode.removeChild(this.popupTarget);
    }
  }

  renderGeoJson(json) {
    function style() {
      return {
        fillColor: '#c3e5ff',
        weight: 1,
        opacity: 0.4,
        color: 'white',
        fillOpacity: 0.8,
      };
    }

    // Render each feature from the GeoJSON to the map
    Object.values(json.features).forEach((feature) => {
      const geoJson = L.geoJSON(feature, {
        // Apply style to feature
        style,

        // For every feature after it's is rendered.
        onEachFeature: (geoJsonFeature, layer) => {
          layer.on({
            click: () => {
              this.openGeoJsonPopup(geoJsonFeature);
            },
          });
        },
      });

      geoJson.addTo(this.map);
    });
  }

  openGeoJsonPopup(geoJsonFeature) {
    this.closePopup();

    // Make sure the GeoJSON contains the required fields...
    if (typeof geoJsonFeature.properties !== 'undefined' && typeof geoJsonFeature.properties.NAME_NL !== 'undefined') {
      const popupElement = Map.createPopupElement({
        translation: {
          title: geoJsonFeature.location?.translation?.title || geoJsonFeature.properties.NAME_NL,
        },
      });

      this.element.appendChild(popupElement);

      popupElement.focus();
    }
  }

  static createPopupElement(item) {
    const div = document.createElement('div');
    div.className = 'map__popup';
    div.setAttribute('data-target', 'map.popup');
    div.setAttribute('tabindex', 0);

    const address = item.street && item.city
      ? `${item.street} ${item.house_number}<br>${item.postal_code} ${item.city}`
      : '';

    const phoneNumber = item.phone_number
      ? `<br>t. <a href="tel:${item.phone_number}">${item.phone_number}</a>`
      : '';

    const email = item.email
      ? `<br>e. <a href="mailto:${item.email}">${item.email}</a>`
      : '';

    const title = item.translation && item.translation.title
      ? `<div class="map__popup-title">${item.translation.title}</div>`
      : '';

    const description = item.translation && item.translation.contact_description
      ? `<div class="cms-content mt-3">${item.translation.contact_description}</div>`
      : '';

    div.innerHTML = `
      <button class="map__popup-close btn--reset" type="button" data-action="click->map#onPopupCloseClick">
        <svg class="icon icon-times" aria-hidden="true"><use xlink:href="#icon-times"></use></svg>
        <span class="sr-only">Close</span>
      </button>

      ${title}

      <div>
        ${address}
        ${phoneNumber}
        ${email}
      </div>

      ${description}
    `;

    return div;
  }

  static createMarkerIcon(type) {
    return L.icon({
      iconUrl: `/front_assets/img/map/pin-${type}.svg`,
      iconSize: [32, 41],
      iconAnchor: [16, 41],
      popupAnchor: [-3, -76],
    });
  }

  static loadLeaflet() {
    return new Promise((resolve, reject) => {
      import(/* webpackChunkName: "leaflet" */ 'leaflet')
        .then(resolve)
        .catch(reject);
    });
  }
}

Map.targets = [
  'popup',
  'container',
];

export default Map;
